/**
 * Minimal ABI for the OLD (V1) Gateway, which stays live after V2 activates.
 *
 * WHY THIS EXISTS
 * V2 deliberately does not migrate DNM reward history — if it did, every
 * migrated user could withdraw their lifetime entitlement a second time. So V2
 * stores only what it has paid out itself and asks V1 for the rest:
 *
 *   claimable = (gross_summ / 100) * 0.08
 *             - (v1Gateway.getUser(me).bv.dnm_withdraw + v2.bv.dnm_withdraw)
 *
 * (DnmFaucetLib.withdrawPersonal / withdrawSeller in arandao-contracts.)
 *
 * Migration zeroes V2's `dnm_withdraw`, so any UI that subtracts only V2's
 * figure shows a migrated user their ENTIRE historical entitlement as
 * claimable, and the withdraw then reverts with `DnmEntitlementExceeded`.
 *
 * Only the reads needed for that sum are mirrored here; the old gateway's
 * address comes from `GATEWAY_ABI.oldGateway()` rather than an env var, so it
 * cannot drift from whatever V2 was actually constructed against.
 *
 * STRANDED BALANCES (the other half of what migration leaves behind)
 * `dnm_withdraw` above is a COUNTER of what V1 already paid, and V2 re-derives
 * the entitlement itself, so nothing is lost. `pending_withdraw` and
 * `pending_voucher` are BALANCES — already earned, not yet taken — and V2
 * imports both as zero without ever reading V1 back:
 *
 *   GatewayCoreLib.withdrawWeekUvm   -> require(user.pending_withdraw >= amount)
 *   DnmFaucetLib.withdrawVoucher     -> _validateAmount(amount, user.pending_voucher)
 *
 * Both read V2's own zeroed field. So whatever a user was holding on V1 at the
 * cutover is reachable ONLY by calling V1 directly, which is what the two write
 * functions below are for. Measured on mainnet 2026-08-04: 2,150 UVM across 19
 * users and 16.6 DNM across 20 users.
 *
 * `calculateWeek` is deliberately NOT mirrored. V1 keeps working after cutover,
 * so calling it would accrue fresh V1 rewards for BV that V2 is also paying.
 * Withdrawing a frozen balance is safe; making V1 calculate again is not.
 */

const LEGACY_USER_OUTPUT = {
  name: "user",
  type: "tuple",
  internalType: "struct UsersStruct.User",
  components: [
    { name: "id", type: "uint256", internalType: "uint256" },
    {
      name: "bv",
      type: "tuple",
      internalType: "struct UsersStruct.BusinessValue",
      components: [
        { name: "left", type: "uint256", internalType: "uint256" },
        { name: "right", type: "uint256", internalType: "uint256" },
        { name: "all_left", type: "uint256", internalType: "uint256" },
        { name: "all_right", type: "uint256", internalType: "uint256" },
        { name: "dir", type: "uint256", internalType: "uint256" },
        { name: "level", type: "uint256", internalType: "uint256" },
        { name: "summ", type: "uint256", internalType: "uint256" },
        { name: "dnm_withdraw", type: "uint256", internalType: "uint256" },
      ],
    },
    {
      name: "last_order",
      type: "tuple",
      internalType: "struct UsersStruct.LastOrder",
      components: [
        { name: "date", type: "uint256", internalType: "uint256" },
        { name: "order_id", type: "uint256", internalType: "uint256" },
        { name: "bv_level", type: "uint256", internalType: "uint256" },
      ],
    },
    { name: "depth", type: "uint256", internalType: "uint256" },
    { name: "pending_withdraw", type: "uint256", internalType: "uint256" },
    { name: "levelup_count", type: "uint256", internalType: "uint256" },
    { name: "lastweek", type: "uint256", internalType: "uint256" },
    { name: "step", type: "uint256", internalType: "uint256" },
    { name: "pending_voucher", type: "uint256", internalType: "uint256" },
    { name: "exists", type: "bool", internalType: "bool" },
    { name: "data", type: "bytes32", internalType: "bytes32" },
  ],
} as const;

export const GATEWAY_LEGACY_ABI = [
  {
    type: "function",
    name: "getUser",
    stateMutability: "view",
    inputs: [{ name: "userAddress", type: "address", internalType: "address" }],
    outputs: [LEGACY_USER_OUTPUT],
  },
  {
    type: "function",
    name: "SellerList",
    stateMutability: "view",
    inputs: [{ name: "", type: "address", internalType: "address" }],
    outputs: [
      { name: "id", type: "uint256", internalType: "uint256" },
      { name: "seller_addr", type: "address", internalType: "address" },
      { name: "bv_value", type: "uint256", internalType: "uint256" },
      {
        name: "token_withdraw_value",
        type: "uint256",
        internalType: "uint256",
      },
      { name: "dnm_withdraw", type: "uint256", internalType: "uint256" },
      { name: "exists", type: "bool", internalType: "bool" },
    ],
  },
  {
    type: "function",
    name: "userWithdrawWeekUVM",
    stateMutability: "nonpayable",
    inputs: [{ name: "amount", type: "uint256", internalType: "uint256" }],
    outputs: [],
  },
  {
    type: "function",
    name: "userWithdrawDNMVoucher",
    stateMutability: "nonpayable",
    inputs: [{ name: "amount", type: "uint256", internalType: "uint256" }],
    outputs: [],
  },
] as const;
